/**
 * Provider Exam Service
 * API integration for provider-exam linking
 */
import apiClient from './apiClient';
import {
  ProviderExamsResponse,
  ProviderExamBulkItem,
  ProviderExamBulkResult,
  ProviderExamImportPreview,
  ProviderExamImportResult,
} from '../types/providerExam';

class ProviderExamService {
  private baseUrl = '/provider-exams';

  /**
   * Get linked and available exams for a provider
   */
  async getForProvider(providerId: number): Promise<ProviderExamsResponse> {
    const response = await apiClient.get(`${this.baseUrl}/${providerId}`);
    return response.data;
  }

  /**
   * Bulk create/update provider exam links
   */
  async bulkUpsert(
    providerId: number,
    items: ProviderExamBulkItem[]
  ): Promise<ProviderExamBulkResult> {
    const response = await apiClient.put(`${this.baseUrl}/${providerId}/bulk`, { items });
    return response.data;
  }

  /**
   * Unlink a single exam from a provider
   */
  async unlinkExam(providerId: number, examId: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${providerId}/exams/${examId}`);
  }

  /**
   * Export the provider's linked exams to an .xlsx file
   */
  async exportLinkedExams(providerId: number): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${providerId}/export`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Preview (dry-run) an import spreadsheet for a provider's exam links
   */
  async previewImport(providerId: number, file: File): Promise<ProviderExamImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/${providerId}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Commit a previously previewed import
   */
  async commitImport(providerId: number, file: File, deleteMissing: boolean): Promise<ProviderExamImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('deleteMissing', String(deleteMissing));
    const response = await apiClient.post(`${this.baseUrl}/${providerId}/import/commit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
}

export const providerExamService = new ProviderExamService();
export default providerExamService;
